import { toast } from '@tamagui/toast/v2';
import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import Head from 'expo-router/head';
import React, { useCallback, useRef, useState } from 'react';
import { getTokenValue, View, XStack, YStack } from 'tamagui';

import MembershipCard from '@/components/MembershipCard';
import PSFetchError from '@/components/PSFetchError';
import { PSHeaderBackButton } from '@/components/PSHeaderButtons';
import PageScrollView from '@/components/ui/PageScrollView';
import PSButton from '@/components/ui/PSButton';
import PSSkeleton from '@/components/ui/PSSkeleton';
import PSText from '@/components/ui/PSText';
import { asApiError } from '@/src/api/apiError';
import type { PerksUsage, PerkUsage, UserProfile } from '@/src/api/authApi';
import type {
  Membership,
  MembershipDetail,
  MembershipPayment,
} from '@/src/api/membershipsApi';
import {
  useFetchMembership,
  type FetchMembershipState,
} from '@/src/hooks/useFetchMembership';
import { useMembershipPlans } from '@/src/hooks/useMembershipPlans';
import { openMembershipSheet } from '@/src/lib/razorpayCheckout';
import { useSessionUser } from '@/src/lib/sessionStore';
import { dateParts, formatDateWithYear } from '@/src/utils/dateFormatter';
import PSBox from '@/components/ui/PSBox';

/**
 * ── /my-memberships/[id] — one membership ────────────────────────────────
 *
 * `GET /me/memberships/{id}` — the summary block the list draws, plus the
 * payment ledger. Four blocks, in the order a member asks the questions in:
 *
 *   1. THE CARD — which pass, and what happens to it next. Every future date
 *      lives here and nowhere else.
 *   2. THE NOTICE — the one sentence the dates can't say on their own.
 *   3. WHAT YOU GET — the allowance, and what's left of it.
 *   4. THE ACTIONS, then HISTORY — what you can do, then what has already
 *      happened, oldest first.
 *
 * The history deliberately STOPS at today. It was drawn once with the renewal as
 * a dotted node at the bottom, and that was wrong twice over: the card already
 * gives the renewal date, and a rail that runs into the future invites a member
 * to act on a row that hasn't happened.
 *
 * Which is also why nothing in the history is pressable. A declined charge with
 * successful charges after it is already resolved.
 *
 * ── What is specced ─────────────────────────────────────────────────────
 * The three `phase: 'current'` states, which is what a member is nearly always
 * looking at: renewing, cancelled, and coupon-granted. Plus the one `upcoming`
 * state that a member can be stuck in and act on — a `pending` purchase, which
 * gets the pay/abandon pair.
 *
 * The rest of [[membershipState]]'s branches are honest but unstyled holding
 * states — they draw what the wire says and offer no action, rather than
 * mislabelling themselves as one of the four. Each is next to be specced.
 * ─────────────────────────────────────────────────────────────────────────
 */

// ── What the page says ───────────────────────────────────────────────────

const oldestFirst = (a: MembershipPayment, b: MembershipPayment) =>
  a.occurredAt.localeCompare(b.occurredAt);

const latestPayment = (payments: MembershipPayment[]) =>
  [...payments].sort(oldestFirst).at(-1);

/**
 * What a full cycle costs, off the CATALOG — the only place the figure exists.
 *
 * A held membership carries no price, and the ledger is the wrong place to look
 * for one: the first charge on a mid-month purchase is the part-month addon
 * (₹580.65 against a ₹3,600 plan), so reading the last captured payment quoted
 * the proration back as the renewal.
 *
 * Null when the catalog can't answer, which is a real state rather than a
 * failure: it only returns `is_active` plans, and a member can be sitting on one
 * that was deactivated under them. The card still names the renewal DATE, so the
 * sentence is simply dropped rather than guessed at.
 */
const renewalPriceOf = (plans: Membership[] | null, planId: string) =>
  plans?.find((plan) => plan.id === planId)?.priceWithTax.display ?? null;

/**
 * The buttons the page can offer. WHICH ones is the state's call, not the
 * screen's, and so is the order they come in.
 *
 * `pay` / `abandon` are the pending pair — finish the checkout, or call it off.
 * Both are about the PAYMENT rather than the membership, which is why they don't
 * reuse `cancel`: that one ends a pass the member is already using.
 */
type Action = 'cancel' | 'continue' | 'pay' | 'abandon';

type MembershipState = {
  /** The sentence under the card. Null when the columns already said it all. */
  notice: string | null;
  /** A notice the member is expected to DO something about, not just read. */
  alert: boolean;
  /** In the order they're stacked — the first is the one the page prefers. */
  actions: Action[];
  /**
   * A ledger only means something where money moved. False on the coupon path,
   * where `payments` is empty by definition.
   */
  showPayments: boolean;
};

/**
 * Whether re-subscribing is even possible — the gate on "Continue membership".
 *
 * `POST /membership` 409s for a member who already holds one, and a `pending`
 * purchase counts, so offering the button in either case walks them to a plans
 * page that can only fail. The entitlement lives on the SESSION (`/me`), NOT on
 * this page: a membership can sit cancelled while a different one is live, and
 * that is exactly the state the button must not appear in.
 *
 * This membership itself doesn't count against it. A lone cancelled pass is
 * still reported by `/me` while it runs out the cycle it paid for, and
 * continuing THAT one is the whole reason the button exists.
 */
const canResubscribe = (user: UserProfile | null, membershipId: string) =>
  !user?.pendingMembership &&
  (!user?.membership || user.membership.id === membershipId);

/**
 * The state, the dates and the words for it, in one place, so the blocks below
 * are only layout: the copy for a state belongs with the state, and the screen
 * only picks which one.
 *
 * The order of the tests IS the precedence, and `phase` leads because the server
 * already folded status and the billing cycle into it. `source` beats `status`
 * for the reason it does everywhere: a coupon membership can't renew, wasn't
 * bought at a gateway and can't fail a charge, so none of the branches below it
 * can say anything true about one.
 */
function membershipState(
  membership: MembershipDetail,
  /** What the page knows that the membership itself can't say. */
  context: {
    /** The plan's full-cycle price, or null while the catalog is in the air. */
    renewalPrice: string | null;
    /** See [[canResubscribe]] — false when another membership is already live. */
    canResubscribe: boolean;
  }
): MembershipState {
  const {
    plan,
    phase,
    source,
    status,
    activatedAt,
    renewsAt,
    expiresAt,
    cancelledAt,
    payments,
    checkoutPayload,
  } = membership;

  if (phase === 'past')
    return {
      notice: null,
      alert: false,
      actions: [],
      showPayments: true,
    };

  // A purchase that hasn't been charged yet has no cycle, so it has no dates
  // either — naming the state is the only thing the card can say.
  //
  // `upcoming` is also where a purchase STAYS once it dies. The server derives
  // the phase from the latest billing cycle, and one that was called off before
  // its first charge never had a cycle to put behind it — so nothing ever moves
  // it to `past`, and `status` is the only field that says it's over. Hence the
  // branches inside rather than one sentence for the lot.
  if (phase === 'upcoming') {
    // `pending` is the purchase that never came back from the gateway. The wire
    // cannot tell "never finished paying" from "paid, and the webhook hasn't
    // landed" — both sit here — so the copy has to be true of both, and the
    // resume is offered rather than instructed.
    if (status === 'pending')
      return {
        notice: `Your ${plan.name} starts as soon as this payment is confirmed. If you didn't finish paying, complete it below — and if you did, it'll come through on its own.`,
        alert: false,
        // Only offer the sheet when there's something left to reopen: the
        // payload goes null the moment the membership stops being `pending`.
        actions: checkoutPayload ? ['pay', 'abandon'] : ['abandon'],
        showPayments: true,
      };

    // Called off before it ever started. Neither branch further down can say
    // this: the `cancelled` one is about a pass you're still using up, and
    // `ended` is about one that ran its course. This never ran at all.
    if (status === 'cancelling' || status === 'cancelled')
      return {
        // What the card can't say is that nothing came of the purchase, which is
        // the reassuring half.
        notice:
          'This membership never started — the payment was cancelled before it went through.',
        alert: false,
        actions: [],
        showPayments: true,
      };

    // Everything else that hasn't started: a downgrade deferred to the cycle
    // boundary, and whatever else the wire parks here. Says only what's true of
    // all of them rather than guessing at which — the holding state the module
    // docstring describes.
    return {
      notice: `Your ${plan.name} hasn't started yet.`,
      alert: false,
      actions: [],
      showPayments: true,
    };
  }

  if (source !== 'payment') {
    const how =
      source === 'grant' ? 'granted to you' : 'activated with a coupon';
    return {
      notice: `This membership was ${how}${activatedAt ? ` on ${formatDateWithYear(activatedAt)}` : ''}.${expiresAt ? ` It runs till ${formatDateWithYear(expiresAt)} and won't renew.` : " It won't renew."}`,
      alert: false,
      actions: [],
      // Nothing was ever charged, so the ledger is empty by definition.
      showPayments: false,
    };
  }

  if (status === 'pending_retry' || status === 'halted') {
    const failed = latestPayment(payments);
    return {
      // Names the card that failed and the day access ends, because those are
      // the two things a member needs to decide whether to bother.
      notice: `We couldn't charge your ${failed?.method ?? 'card'} on ${failed ? formatDateWithYear(failed.occurredAt) : 'the renewal date'}.${expiresAt ? ` Your membership ends on ${formatDateWithYear(expiresAt)} unless the payment goes through.` : ''}`,
      alert: true,
      actions: [],
      showPayments: true,
    };
  }

  // `cancelling` is the status the moment the DELETE lands; `cancelled` is the
  // same state once the webhook has confirmed it. Only the latter carries a
  // `cancelledAt`, so the day is named when there is one and skipped when not.
  if (status === 'cancelling' || status === 'cancelled')
    return {
      notice: `${cancelledAt ? `You cancelled on ${formatDateWithYear(cancelledAt)}.` : `Your ${plan.name} is cancelled.`} You keep your benefits till ${expiresAt ? formatDateWithYear(expiresAt) : 'the end of this cycle'}, and it won't renew after that.`,
      alert: false,
      // Nothing to continue while another membership is live — see the gate.
      actions: context.canResubscribe ? ['continue'] : [],
      showPayments: true,
    };

  return {
    // Null when there's nothing to ADD — the card already gave the date, so a
    // sentence that only repeated it would be noise.
    notice:
      context.renewalPrice && renewsAt
        ? `${context.renewalPrice} will be charged on ${formatDateWithYear(renewsAt)}.`
        : null,
    alert: false,
    // A one-shot pass (the Trial Pass) has nothing to cancel — it runs to its
    // `expiresAt` and stops on its own.
    actions: expiresAt ? [] : ['cancel'],
    showPayments: true,
  };
}

/**
 * The sentence under the card.
 *
 * `alert` is a hairline in `$error` rather than a red fill: the membership is
 * still working today, and a red block would say it had already stopped.
 */
function StateNotice({ state }: { state: MembershipState }) {
  if (!state.notice) return null;

  return (
    <PSBox>
      <PSText.Body2 color="$textDark">{state.notice}</PSText.Body2>
    </PSBox>
  );
}

/**
 * What a member can do to this membership, sitting between what they have and
 * what they've paid — the last thing read before the ledger, not the first thing
 * seen under the card.
 *
 * `cancel` and `continue` are `outlineBrand`: cancelling isn't destructive (they
 * keep the pass to the end of the cycle they paid for) and re-subscribing isn't
 * this page's job, so neither earns a filled button. `pay` does — finishing the
 * purchase is the outcome this page wants — and the "Cancel payment" under it
 * drops to the neutral outline so the pair reads as a preference rather than as
 * two equal choices.
 */
function MembershipActions({
  actions,
  membership,
}: {
  actions: Action[];
  membership: MembershipDetail;
}) {
  const router = useRouter();
  const user = useSessionUser();
  const [isPaying, setIsPaying] = useState(false);

  const checkout = membership.checkoutPayload;
  const cancelHref = `/my-memberships/${membership.id}/cancel` as const;

  /**
   * Reopen the sheet this purchase was created with.
   *
   * NOT a second `POST /membership`: the mandate already exists at Razorpay, and
   * posting again would authorize another one. Handing the payload back for as
   * long as the membership is `pending` is the server's way of saying "resume
   * this", and this is the only screen that takes it up.
   */
  const handlePay = async () => {
    if (!checkout || !user) return;

    setIsPaying(true);
    try {
      const outcome = await openMembershipSheet({
        checkout,
        planName: membership.plan.name,
        payer: {
          name: user.fullName,
          // Null only on an unfinished profile, which couldn't have got a
          // membership created in the first place.
          email: user.email ?? '',
          contact: user.mobile,
        },
      });

      // Backing out is not a failure: they're exactly where they started, with
      // the same payment still outstanding and the same two buttons.
      if (outcome.status === 'cancelled') {
        setIsPaying(false);
        return;
      }

      // The sheet closing proves nothing — the poll is what decides. `replace`,
      // because this page is stale the moment the payment lands.
      router.replace({
        pathname: '/memberships/purchase/status',
        params: { id: membership.id, plan: membership.plan.id },
      });
    } catch (caught) {
      toast.error(asApiError(caught).message);
      setIsPaying(false);
    }
  };

  const button = (action: Action) => {
    switch (action) {
      case 'pay':
        return (
          <PSButton
            key={action}
            title="Complete payment"
            loading={isPaying}
            loadingTitle="Completing"
            fullWidth
            onPress={handlePay}
          />
        );
      case 'abandon':
        return (
          <PSButton
            key={action}
            title="Cancel payment"
            variant="outline"
            disabled={isPaying}
            fullWidth
            // The same confirm step the cancel button uses — it reads the
            // membership itself and says the right paragraph for a purchase that
            // never started.
            onPress={() => router.push(cancelHref)}
          />
        );
      case 'continue':
        return (
          <PSButton
            key={action}
            title="Continue membership"
            variant="outlineBrand"
            fullWidth
            // Straight to the plans rather than the top of the sales page: they
            // already know what it is, they're re-buying it.
            onPress={() => router.push('/memberships?section=plans')}
          />
        );
      case 'cancel':
        return (
          <PSButton
            key={action}
            title="Cancel membership"
            variant="destructive"
            fullWidth
            // A confirm step of its own rather than an inline "are you sure":
            // that screen has to say what they keep and until when, which is a
            // paragraph.
            onPress={() => router.push(cancelHref)}
          />
        );
    }
  };

  return <YStack gap="$md">{actions.map(button)}</YStack>;
}

// ── What you get ─────────────────────────────────────────────────────────

const perkValue = (perk: PerkUsage) => {
  if (perk.isUnlimited) return 'Unlimited';
  return perk.remaining > 0
    ? `${perk.remaining} of ${perk.limit} left`
    : 'None left';
};

/**
 * The allowance as a ledger: what the category is, and what's left of it.
 * REMAINING rather than used — the number a member acts on is the one they can
 * still spend.
 *
 * A spent category stays on the table and goes muted. Dropping it would make the
 * list shorter every time someone books, and "Secret Menu, none left" is the row
 * worth reading.
 *
 * `resetsAt` null means the allowance doesn't reset, it stops — a coupon grant
 * can run for a year, so calling that a month would be wrong.
 */
function PerksTable({ perks }: { perks: PerksUsage }) {
  // A zero-limit perk is one the plan doesn't include, not one that's spent.
  const rows = Object.entries(perks.perks).filter(
    ([, perk]) => perk.isUnlimited || perk.limit > 0
  );
  if (rows.length === 0) return null;

  return (
    <YStack gap="$md">
      <XStack justifyContent="space-between" alignItems="center">
        <PSText.H2 color="$textDark">
          {perks.resetsAt ? 'Usage this month' : 'Usage'}
        </PSText.H2>
        {perks.resetsAt && (
          <PSText.Body3 color="$textDarkMuted">
            {`Resets on ${formatDateWithYear(perks.resetsAt)}`}
          </PSText.Body3>
        )}
      </XStack>
      <PSBox gap={'$space.md'}>
        {rows.map(([key, perk], i) => (
          <React.Fragment key={key}>
            <XStack key={key} gap="$md" items="center">
              <PSText.Body2
                flex={1}
                color={perk.canBook ? '$textDark' : '$textDarkMuted'}
              >
                {perk.label}
              </PSText.Body2>
              <PSText.Body3
                color={perk.canBook ? '$textDark' : '$textDarkMuted'}
              >
                {perkValue(perk)}
              </PSText.Body3>
            </XStack>

            {i < rows.length - 1 && (
              <View
                borderTopWidth={'$borderWidth.hairline'}
                borderTopColor={'$borderColor'}
              />
            )}
          </React.Fragment>
        ))}
      </PSBox>
    </YStack>
  );
}

// ── History ──────────────────────────────────────────────────────────────

// The rail's geometry. Every node centres in a column of one width, so the line
// can't kink. The nudge drops the node onto the first line of the title beside
// it (half the difference between body2's 22px line box and the node).
const DATE_COL = 36;
const RAIL_COL = 16;
const RAIL = 1;
const NODE = 10;
const NODE_NUDGE = 6;

type Entry = {
  id: string;
  at: string;
  title: string;
  amount: string | null;
  detail: string | null;
  failed: boolean;
};

const PAYMENT_TITLE: Record<MembershipPayment['status'], string> = {
  captured: 'Payment received',
  failed: 'Payment failed',
  created: 'Payment not completed',
};

/**
 * What has already happened, oldest first — the membership starting, every
 * charge against it, and its ending if it has one. Nothing here is in the future
 * and nothing here is pressable.
 *
 * The wire sends no failure REASON, so a failed row's second line is the method
 * it was attempted on, same as a captured one's.
 */
function buildHistory(membership: MembershipDetail): Entry[] {
  const entries: Entry[] = [];

  if (membership.activatedAt)
    entries.push({
      id: 'start',
      at: membership.activatedAt,
      title: 'Membership started',
      amount: null,
      detail: null,
      failed: false,
    });

  for (const payment of [...membership.payments].sort(oldestFirst))
    entries.push({
      id: payment.id,
      at: payment.occurredAt,
      title: PAYMENT_TITLE[payment.status],
      amount: payment.amount.display,
      detail: payment.method,
      failed: payment.status === 'failed',
    });

  if (membership.phase === 'past' && membership.expiresAt)
    entries.push({
      id: 'end',
      at: membership.expiresAt,
      title: 'Membership ended',
      amount: null,
      detail: null,
      failed: false,
    });

  return entries;
}

const sameDay = (a: string, b: string) => {
  const [x, y] = [dateParts(a), dateParts(b)];
  return x.day === y.day && x.month === y.month && x.year === y.year;
};

/**
 * Dates in their own right-aligned column against the rail, so the chronology is
 * something you scan rather than a label you read on each row. A repeated date is
 * left blank the way a timetable does it — a membership starting and its first
 * charge are one moment, not two.
 */
function History({ entries }: { entries: Entry[] }) {
  return (
    <YStack gap="$md">
      <PSText.H2 color="$textDark">Payment History</PSText.H2>
      <YStack>
        {entries.map((entry, i) => {
          const at = dateParts(entry.at);
          const repeated = i > 0 && sameDay(entries[i - 1].at, entry.at);
          const last = i === entries.length - 1;

          return (
            <XStack key={entry.id} gap="$md">
              <YStack width={DATE_COL} items="flex-end">
                {!repeated && (
                  <>
                    <PSText.Body1 color="$textDark">{at.day}</PSText.Body1>
                    <PSText.H3 color="$textDarkMuted">{at.month}</PSText.H3>
                  </>
                )}
              </YStack>

              <YStack width={RAIL_COL} items="center">
                <View
                  width={NODE}
                  height={NODE}
                  mt={NODE_NUDGE}
                  rounded="$full"
                  bg={entry.failed ? '$error' : '$brand'}
                />
                {!last && <YStack flex={1} width={RAIL} bg="$borderColor" />}
              </YStack>

              <YStack flex={1} gap="$xs" pb="$xl">
                <XStack gap="$md" items="flex-start">
                  <PSText.Body2
                    flex={1}
                    color={entry.failed ? '$error' : '$textDark'}
                  >
                    {entry.title}
                  </PSText.Body2>
                  {entry.amount && (
                    <PSText.Body2 color="$textDark">
                      {entry.amount}
                    </PSText.Body2>
                  )}
                </XStack>
                {entry.detail && (
                  <PSText.Body4 color="$textDarkMuted">
                    {entry.detail}
                  </PSText.Body4>
                )}
              </YStack>
            </XStack>
          );
        })}
      </YStack>
    </YStack>
  );
}

// ── The states around the page ───────────────────────────────────────────

// The card, then the two blocks under it. A bone already wears the card's corner.
const CARD_BONE_HEIGHT = 128;

function MembershipSkeleton() {
  return (
    <PSSkeleton>
      <YStack alignSelf="stretch" gap="$xxl">
        <PSSkeleton.Bone style={{ width: '100%', height: CARD_BONE_HEIGHT }} />
        <PSSkeleton.Bone style={{ width: '100%', height: 160 }} />
        <PSSkeleton.Bone style={{ width: '100%', height: 120 }} />
      </YStack>
    </PSSkeleton>
  );
}

/**
 * The page body, branched on the fetch. A 404 — someone else's id, or a
 * malformed one — arrives here as an ordinary error carrying the server's own
 * "Membership not found."
 */
function MembershipBody({ state }: { state: FetchMembershipState }) {
  // The catalog, for the renewal price and nothing else — see [[renewalPriceOf]].
  // Fetched unconditionally because a hook can't be, and in parallel with the
  // membership rather than after it. Its own failure is silent: the page is
  // about the membership, and a missing price costs one sentence.
  const { plans } = useMembershipPlans();
  // The entitlement, which is a fact about the USER and not about this row.
  const user = useSessionUser();

  if (state.status === 'loading') return <MembershipSkeleton />;

  if (state.status === 'error')
    return <PSFetchError message={state.error.message} onRetry={state.retry} />;

  const membership = state.membership;
  const view = membershipState(membership, {
    renewalPrice: renewalPriceOf(plans, membership.plan.id),
    canResubscribe: canResubscribe(user, membership.id),
  });
  const entries = buildHistory(membership);

  return (
    <YStack gap="$xxl">
      <YStack gap="$lg">
        <MembershipCard membership={membership} />
        <StateNotice state={view} />
      </YStack>

      {/* Null unless the status is exactly `active`, which means UNKNOWN rather
          than "nothing left" — a cancelled membership's perks still work. So the
          block is dropped rather than drawn empty. */}
      {membership.perksUsage && <PerksTable perks={membership.perksUsage} />}

      {/* One entry is the start node alone — a history that only repeats what
          the card already said. */}
      {view.showPayments && entries.length > 1 && <History entries={entries} />}
      {view.actions.length > 0 && (
        <MembershipActions actions={view.actions} membership={membership} />
      )}
    </YStack>
  );
}

// ── The screen ───────────────────────────────────────────────────────────

export default function MembershipDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const state = useFetchMembership(id);
  const { refresh } = state;
  const [isRefreshing, setIsRefreshing] = useState(false);

  const onRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refresh();
    setIsRefreshing(false);
  }, [refresh]);

  // Cancelling answers 204 with no body, so coming back from the confirm step is
  // the only signal that this membership changed. The first focus is skipped —
  // the mount fetch is already in the air.
  const hasFocused = useRef(false);
  useFocusEffect(
    useCallback(() => {
      if (hasFocused.current) refresh();
      hasFocused.current = true;
    }, [refresh])
  );

  return (
    <>
      <Head>
        <title>Membership · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerTransparent: false,
          headerTitle: () => (
            <PSText.H1 color="$textDark" textAlign="center">
              Membership
            </PSText.H1>
          ),
          headerStyle: { backgroundColor: getTokenValue('$background') },
          headerLeft: () => <PSHeaderBackButton />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButton />,
              hidesSharedBackground: true,
            },
          ],
        }}
      />
      <PageScrollView
        headerTransparent={false}
        isRefreshing={isRefreshing}
        onRefresh={onRefresh}
      >
        <MembershipBody state={state} />
      </PageScrollView>
    </>
  );
}
